import React, { Component, Fragment } from "react";
import { observer, inject } from "mobx-react";
import ModalContainer from "../../components/modal/ModalContainer";
import ReactToPrint from "react-to-print";
import moment from "moment";
import currencyHelper from "../../helper/currency";
import { ProfileType, SuperLedgerStatus } from "../../types/enum";
import hgSuperPayStore from "../../stores/hgSuperPayStore";
import {
    DialogContent,
    DialogActions,
    Button,
    Dialog,
    DialogTitle,
    TextField,
    Checkbox,
    FormControlLabel,
    Typography,
    CircularProgress,
    Chip
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "material-ui-pickers";
import MomentUtils from "@date-io/moment";
import EventIcon from "@material-ui/icons/Event";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import Tooltip from "@material-ui/core/Tooltip";

const printStyles = {
    container: {
        padding: "20px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: "Arial, sans-serif"
    },
    header: {
        marginBottom: "30px",
        textAlign: "center",
        backgroundColor: "#ffffff"
    },
    title: {
        fontWeight: "bold",
        marginBottom: "10px",
        fontSize: "24px",
        color: "#000000",
        backgroundColor: "#ffffff"
    },
    subtitle: {
        color: "#666666",
        fontSize: "16px",
        marginBottom: "5px",
        backgroundColor: "#ffffff"
    },
    bodyText: {
        color: "#666666",
        fontSize: "14px",
        backgroundColor: "#ffffff"
    },
    divider: {
        borderTop: "1px solid #dddddd",
        marginBottom: "20px",
        backgroundColor: "#ffffff"
    },
    sectionTitle: {
        fontWeight: "bold",
        marginBottom: "15px",
        fontSize: "18px",
        color: "#000000",
        backgroundColor: "#ffffff"
    },
    summaryBox: {
        padding: "20px",
        marginBottom: "20px",
        backgroundColor: "#f5f5f5",
        border: "1px solid #dddddd",
        borderRadius: "4px"
    },
    summaryLabel: {
        color: "#666666",
        fontSize: "14px",
        marginBottom: "5px",
        backgroundColor: "transparent"
    },
    summaryValue: {
        fontSize: "18px",
        fontWeight: "normal",
        color: "#000000",
        backgroundColor: "transparent"
    },
    summaryTotal: {
        fontSize: "22px",
        fontWeight: "bold",
        color: "#ff0c9c",
        backgroundColor: "transparent",
        wordBreak: "break-word",
        overflowWrap: "break-word"
    },
    paymentCard: {
        padding: "15px",
        marginBottom: "10px",
        backgroundColor: "#ffffff",
        border: "1px solid #dddddd",
        borderRadius: "4px"
    },
    paymentLabel: {
        color: "#666666",
        fontSize: "12px",
        marginBottom: "2px",
        backgroundColor: "#ffffff"
    },
    paymentValue: {
        fontSize: "14px",
        color: "#000000",
        backgroundColor: "#ffffff"
    },
    paymentValueBold: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#000000",
        backgroundColor: "#ffffff"
    },
    paymentAmount: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#ff0c9c",
        backgroundColor: "#ffffff",
        wordBreak: "break-word",
        overflowWrap: "break-word"
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        backgroundColor: "#ffffff"
    },
    tableHeader: {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
        padding: "10px 8px",
        textAlign: "left",
        borderBottom: "2px solid #dddddd",
        color: "#000000",
        fontSize: "13px"
    },
    tableHeaderRight: {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
        padding: "10px 8px",
        textAlign: "right",
        borderBottom: "2px solid #dddddd",
        color: "#000000",
        fontSize: "13px"
    },
    tableCell: {
        padding: "10px 8px",
        textAlign: "left",
        borderBottom: "1px solid #eeeeee",
        color: "#000000",
        backgroundColor: "#ffffff",
        fontSize: "13px"
    },
    tableCellRight: {
        padding: "10px 8px",
        textAlign: "right",
        borderBottom: "1px solid #eeeeee",
        color: "#000000",
        backgroundColor: "#ffffff",
        fontSize: "13px"
    },
    tableFooter: {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
        padding: "10px 8px",
        color: "#000000",
        fontSize: "13px"
    },
    tableFooterRight: {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
        padding: "10px 8px",
        textAlign: "right",
        color: "#000000",
        fontSize: "13px"
    },
    emptyMessage: {
        textAlign: "center",
        padding: "40px",
        color: "#999999",
        backgroundColor: "#ffffff"
    },
    gridRow: {
        display: "flex",
        flexWrap: "wrap",
        marginBottom: "10px",
        backgroundColor: "#ffffff"
    },
    gridCol6: {
        width: "50%",
        padding: "5px",
        boxSizing: "border-box",
        backgroundColor: "transparent"
    }
};

function formatHgDob(emp) {
    if (!emp) return "";
    if (emp.dobDay && emp.dobMonth && emp.dobYear) {
        return `${String(emp.dobDay).padStart(2, "0")}/${String(emp.dobMonth).padStart(2, "0")}/${emp.dobYear}`;
    }
    return emp.dob || "";
}

function formatHgAddress(emp) {
    if (!emp) return "";
    return [emp.addressLine1, emp.addressLine2, emp.addressLine3, emp.addressLine4, emp.suburb, emp.state, emp.postcode]
        .filter(Boolean).join(", ");
}

@inject("profileStore", "hgBillingStore", "superLedgerStore", "reportStore", "templateStore", "hgSuperPayStore", "financialProfileStore")
@observer
class SuperLedgerReportModal extends Component {
    constructor(props) {
        super(props);
        this.printRef = React.createRef();
        this.state = {
            paymentDialogOpen: false,
            paymentDate: moment(),
            paymentNotes: "",
            selectedLedgerIds: [],
            isSubmitting: false,
            hgSuperPayDialogOpen: false,
            hgSuperPayStep: "check",
            hgSuperPayLoading: false,
            hgSuperPayError: null,
            payeeEmployeeStatus: null,
            hgSuperPayData: {}
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            const unpaidLedgers = this.getUnpaidLedgers();
            this.setState({
                selectedLedgerIds: unpaidLedgers.map(l => l.id),
                paymentDate: moment(),
                paymentNotes: "",
                hgSuperPayData: {}
            });
            this._loadHgSuperPayData();
        }
    }

    _loadHgSuperPayData = () => {
        const { currentProfile } = this.props.profileStore;
        const employerProfileId = currentProfile ? currentProfile.id : null;
        if (!employerProfileId) return;
        const filteredLedgers = this.getFilteredLedgers();
        const summary = this.calculateSummary(filteredLedgers);
        Object.values(summary.payeeGroups).forEach(group => {
            if (!group.id || group.id === "unknown") return;
            this.setState(prev => ({
                hgSuperPayData: { ...prev.hgSuperPayData, [group.id]: { loading: true, data: null, error: null } }
            }));
            this.props.hgSuperPayStore.getEmployeePreview(group.id, employerProfileId)
                .then(result => {
                    this.setState(prev => ({
                        hgSuperPayData: { ...prev.hgSuperPayData, [group.id]: { loading: false, data: result.employeeData ? { ...result.employeeData, hasTfn: result.hasTfn } : null, error: null } }
                    }));
                })
                .catch(() => {
                    this.setState(prev => ({
                        hgSuperPayData: { ...prev.hgSuperPayData, [group.id]: { loading: false, data: null, error: "Not registered" } }
                    }));
                });
        });
    };

    getCurrencySymbol = () => {
        const { currentProfile } = this.props.profileStore;
        if ((currentProfile?.type === ProfileType.Musician.ordinal || currentProfile?.type === ProfileType.Agent.ordinal) && currentProfile?.currency) {
            return currentProfile?.currency === "GBP" ? "£" : "$";
        } else if (currentProfile?.type === ProfileType.Staff.ordinal) {
            if (currentProfile.hasOrganisation) {
                return currentProfile.organisation[0]?.currency === "GBP" ? "£" : "$";
            } else {
                return currentProfile.venue?.currency === "GBP" ? "£" : "$";
            }
        }
        return "$";
    };

    getFilteredLedgers = () => {
        const { ledgers } = this.props;
        if (!ledgers) return [];
        return ledgers.filter(ledger => ledger && ledger.invoice && ledger.invoice.id);
    };

    getUnpaidLedgers = () => {
        const filteredLedgers = this.getFilteredLedgers();
        return filteredLedgers.filter(ledger =>
            ledger.status === SuperLedgerStatus.Pending.ordinal ||
            ledger.status === SuperLedgerStatus.Refunded.ordinal ||
            ledger.status === SuperLedgerStatus.Failed.ordinal
        );
    };

    calculateSummary = (filteredLedgers) => {
        const summary = {
            totalSuperFee: 0,
            ledgerCount: 0,
            payeeGroups: {}
        };

        filteredLedgers.forEach(ledger => {
            summary.totalSuperFee += parseFloat(ledger.superFee || 0);
            summary.ledgerCount++;

            const payeeId = ledger.payee?.id || 'unknown';
            if (!summary.payeeGroups[payeeId]) {
                const superProfile = ledger.payee?.superAnnuationProfile || {};
                const paymentDetails = ledger.payee?.paymentDetails || {};
                summary.payeeGroups[payeeId] = {
                    id: payeeId,
                    payeeName: ledger.payee?.profileName || `${ledger.payee?.firstName || ''} ${ledger.payee?.lastName || ''}`.trim() || 'Unknown',
                    superFund: superProfile.fundName || 'N/A',
                    productName: superProfile.productName || 'N/A',
                    abn: superProfile.aBN || 'N/A',
                    usi: superProfile.uSI || 'N/A',
                    memberNumber: superProfile.superFundMemberNumber || 'N/A',
                    bsb: paymentDetails.bsb || paymentDetails.bSB || superProfile.bSB || ledger.payee?.bsb || 'N/A',
                    accountNumber: paymentDetails.accountNumber || superProfile.accountNumber || ledger.payee?.accountNumber || 'N/A',
                    accountName: paymentDetails.accountName || superProfile.accountName || ledger.payee?.accountName || 'N/A',
                    currency: ledger.payee?.currency || 'AUD',
                    totalAmount: 0,
                    ledgerCount: 0
                };
            }
            summary.payeeGroups[payeeId].totalAmount += parseFloat(ledger.superFee || 0);
            summary.payeeGroups[payeeId].ledgerCount++;
        });

        return summary;
    };

    getDateRangeText = () => {
        const { startDate, endDate, filterType } = this.props.reportStore;
        if (filterType === "quarterly") {
            const m = moment(startDate);
            return `Q${m.quarter()} ${m.year()}`;
        } else if (filterType === "monthly") {
            return moment(startDate).format("MMMM YYYY");
        } else if (startDate && endDate) {
            return `${moment(startDate).format("DD MMM YYYY")} - ${moment(endDate).format("DD MMM YYYY")}`;
        }
        return "All Time";
    };

    handleOpenPaymentDialog = () => {
        const { ledgerType } = this.props;
        if (ledgerType === "outgoing") return;
        
        const unpaidLedgers = this.getUnpaidLedgers();
        this.setState({
            paymentDialogOpen: true,
            selectedLedgerIds: unpaidLedgers.map(l => l.id),
            paymentDate: moment(),
            paymentNotes: ""
        });
    };

    handleClosePaymentDialog = () => {
        this.setState({
            paymentDialogOpen: false,
            selectedLedgerIds: [],
            paymentNotes: ""
        });
    };

    handlePaymentDateChange = (date) => {
        this.setState({ paymentDate: date });
    };

    handlePaymentNotesChange = (e) => {
        this.setState({ paymentNotes: e.target.value });
    };

    handleLedgerCheckboxChange = (ledgerId) => {
        const { selectedLedgerIds } = this.state;
        if (selectedLedgerIds.includes(ledgerId)) {
            this.setState({
                selectedLedgerIds: selectedLedgerIds.filter(id => id !== ledgerId)
            });
        } else {
            this.setState({
                selectedLedgerIds: [...selectedLedgerIds, ledgerId]
            });
        }
    };

    handleSelectAllLedgers = () => {
        const unpaidLedgers = this.getUnpaidLedgers();
        const allIds = unpaidLedgers.map(l => l.id);
        const { selectedLedgerIds } = this.state;
        
        if (selectedLedgerIds.length === allIds.length) {
            this.setState({ selectedLedgerIds: [] });
        } else {
            this.setState({ selectedLedgerIds: allIds });
        }
    };

    handleSubmitPayment = () => {
        const { selectedLedgerIds, paymentDate, paymentNotes } = this.state;
        const { ledgerType, onClose } = this.props;
        const { currentProfile } = this.props.profileStore;
        const unpaidLedgers = this.getUnpaidLedgers();
        
        if (ledgerType === "outgoing") return;
        
        if (selectedLedgerIds.length === 0) {
            this.props.templateStore.openSnackbar("Please select at least one ledger to pay");
            return;
        }

        const selectedLedgers = unpaidLedgers.filter(l => selectedLedgerIds.includes(l.id));
        if (selectedLedgers.length === 0) return;

        const firstLedger = selectedLedgers[0];
        const firstPayerId = firstLedger.payer?.id;
        const firstPayeeId = firstLedger.payee?.id;

        const hasMixedPayees = selectedLedgers.some(l => l.payee?.id !== firstPayeeId);
        const hasMixedPayers = selectedLedgers.some(l => l.payer?.id !== firstPayerId);

        if (hasMixedPayees || hasMixedPayers) {
            this.props.templateStore.openSnackbar("All selected ledgers must have the same payer and payee. Please adjust your selection.");
            return;
        }

        const payerId = ledgerType === "incoming" ? currentProfile?.id : firstPayerId;
        const payeeId = ledgerType === "incoming" ? firstPayeeId : currentProfile?.id;

        const data = {
            payerId: payerId,
            payeeId: payeeId,
            superLedgerIds: selectedLedgerIds,
            paymentMethod: "Bank Transfer",
            paymentDate: moment(paymentDate).toISOString(),
            sourceType: "MANUAL",
            notes: paymentNotes,
            createdByProfileId: currentProfile?.id
        };

        this.setState({ isSubmitting: true });

        this.props.superLedgerStore.paySuperLedgersManually(data)
            .then(() => {
                this.props.templateStore.openSnackbar("Super ledgers marked as paid successfully");
                this.props.superLedgerStore.markLedgersAsPaidInStore(selectedLedgerIds);
                this.handleClosePaymentDialog();
                if (onClose) onClose();
            })
            .catch((error) => {
                this.props.templateStore.openSnackbar("Failed to mark ledgers as paid. Please try again.");
                console.error("Payment error:", error);
            })
            .finally(() => {
                this.setState({ isSubmitting: false });
            });
    };

    handleOpenHgSuperPayDialog = () => {
        const unpaidLedgers = this.getUnpaidLedgers();
        
        if (unpaidLedgers.length === 0) {
            this.props.templateStore.openSnackbar("No unpaid ledgers available");
            return;
        }

        const firstLedger = unpaidLedgers[0];
        const firstPayerId = firstLedger.payer?.id;
        const firstPayeeId = firstLedger.payee?.id;

        const hasMixedPayees = unpaidLedgers.some(l => l.payee?.id !== firstPayeeId);
        const hasMixedPayers = unpaidLedgers.some(l => l.payer?.id !== firstPayerId);

        if (hasMixedPayees || hasMixedPayers) {
            this.props.templateStore.openSnackbar("All ledgers must have the same payer and payee to process payment");
            return;
        }

        this.setState({
            hgSuperPayDialogOpen: true,
            hgSuperPayStep: "check",
            hgSuperPayLoading: true,
            hgSuperPayError: null,
            selectedLedgerIds: unpaidLedgers.map(l => l.id)
        });

        const payeeProfileId = firstPayeeId;

        if (!payeeProfileId) {
            this.setState({
                hgSuperPayLoading: false,
                hgSuperPayError: "No payee found for these ledgers"
            });
            return;
        }

        hgSuperPayStore.getEmployeeStatus(payeeProfileId)
            .then((status) => {
                if (status.registered) {
                    this.setState({
                        hgSuperPayStep: "validate",
                        payeeEmployeeStatus: status
                    });
                    this.handleValidateLedgers();
                } else {
                    this.setState({
                        hgSuperPayStep: "sync",
                        hgSuperPayLoading: false,
                        payeeEmployeeStatus: status
                    });
                }
            })
            .catch((error) => {
                this.setState({
                    hgSuperPayStep: "sync",
                    hgSuperPayLoading: false,
                    payeeEmployeeStatus: { registered: false }
                });
            });
    };

    handleCloseHgSuperPayDialog = () => {
        this.setState({
            hgSuperPayDialogOpen: false,
            hgSuperPayStep: "check",
            hgSuperPayLoading: false,
            hgSuperPayError: null
        });
    };

    handleSyncEmployee = () => {
        const unpaidLedgers = this.getUnpaidLedgers();
        const firstLedger = unpaidLedgers[0];
        const payeeProfileId = firstLedger?.payee?.id;

        if (!payeeProfileId) {
            this.setState({ hgSuperPayError: "No payee profile found" });
            return;
        }

        this.setState({ hgSuperPayLoading: true, hgSuperPayError: null });

        hgSuperPayStore.syncEmployee(payeeProfileId)
            .then(() => {
                this.setState({
                    hgSuperPayStep: "validate",
                    payeeEmployeeStatus: { registered: true }
                });
                this.handleValidateLedgers();
            })
            .catch((error) => {
                this.setState({
                    hgSuperPayLoading: false,
                    hgSuperPayError: error?.response?.data?.message || "Failed to sync employee"
                });
            });
    };

    handleValidateLedgers = () => {
        const { selectedLedgerIds } = this.state;

        this.setState({ hgSuperPayLoading: true, hgSuperPayError: null });

        hgSuperPayStore.validateLedgers(selectedLedgerIds)
            .then((result) => {
                // Response may be wrapped in a `response` envelope
                const data = result?.response || result || {};
                if (data.valid) {
                    this.setState({
                        hgSuperPayStep: "confirm",
                        hgSuperPayLoading: false
                    });
                } else {
                    const payerErrors = data.payerErrors || [];
                    const errorMsg = payerErrors.length > 0
                        ? payerErrors.map(e => e.error).join("\n")
                        : (data.error || "Ledger validation failed");
                    this.setState({
                        hgSuperPayLoading: false,
                        hgSuperPayError: errorMsg
                    });
                }
            })
            .catch((error) => {
                this.setState({
                    hgSuperPayLoading: false,
                    hgSuperPayError: error?.response?.data?.message || "Failed to validate ledgers"
                });
            });
    };

    handleSubmitHgSuperPayPayment = async () => {
        const { selectedLedgerIds, paymentNotes } = this.state;
        const { currentProfile } = this.props.profileStore;
        const { onClose } = this.props;

        this.setState({ hgSuperPayLoading: true, hgSuperPayError: null });

        try {
            // Derive the raw payer ID from the selected ledger data.
            // Effective-payer resolution is handled inside hgSuperPayStore.submitPayment.
            const unpaidLedgers = this.getUnpaidLedgers();
            const selectedLedgers = unpaidLedgers.filter(l => selectedLedgerIds.includes(l.id));
            const rawPayerId = selectedLedgers.length > 0
                ? (selectedLedgers[0].payer?.id || currentProfile?.id)
                : currentProfile?.id;

            const data = {
                payerId: rawPayerId,
                superLedgerIds: selectedLedgerIds,
                notes: paymentNotes || "Super payment processed"
            };

            await hgSuperPayStore.submitPayment(data);
            this.props.superLedgerStore.markLedgersAsPaidInStore(selectedLedgerIds);
            this.handleCloseHgSuperPayDialog();
            if (onClose) onClose();
        } catch (error) {
            this.setState({
                hgSuperPayLoading: false,
                hgSuperPayError: error?.response?.data?.message || "Failed to submit payment"
            });
        }
    };

    getPayeeName = () => {
        const unpaidLedgers = this.getUnpaidLedgers();
        const firstLedger = unpaidLedgers[0];
        return firstLedger?.payee?.profileName || 
               `${firstLedger?.payee?.firstName || ''} ${firstLedger?.payee?.lastName || ''}`.trim() || 
               'Unknown';
    };

    renderHgSuperPayDialog = () => {
        const { hgSuperPayDialogOpen, hgSuperPayStep, hgSuperPayLoading, hgSuperPayError, selectedLedgerIds } = this.state;
        const unpaidLedgers = this.getUnpaidLedgers();
        const currencySymbol = this.getCurrencySymbol();
        const payeeName = this.getPayeeName();

        const selectedTotal = unpaidLedgers
            .filter(l => selectedLedgerIds.includes(l.id))
            .reduce((sum, l) => sum + parseFloat(l.superFee || 0), 0);

        return (
            <Dialog
                open={hgSuperPayDialogOpen}
                onClose={this.handleCloseHgSuperPayDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Process Payment</DialogTitle>
                <DialogContent>
                    {hgSuperPayLoading && (
                        <div style={{ textAlign: "center", padding: "40px" }}>
                            <CircularProgress />
                            <Typography style={{ marginTop: "20px" }}>
                                {hgSuperPayStep === "check" && "Checking employee status..."}
                                {hgSuperPayStep === "sync" && "Syncing employee..."}
                                {hgSuperPayStep === "validate" && "Validating ledgers..."}
                                {hgSuperPayStep === "confirm" && "Submitting payment..."}
                            </Typography>
                        </div>
                    )}

                    {!hgSuperPayLoading && hgSuperPayError && (
                        <div style={{ padding: "20px", textAlign: "center" }}>
                            <Typography color="error" style={{ marginBottom: "20px" }}>
                                {hgSuperPayError}
                            </Typography>
                            <Button onClick={this.handleCloseHgSuperPayDialog} color="primary">
                                Close
                            </Button>
                        </div>
                    )}

                    {!hgSuperPayLoading && !hgSuperPayError && hgSuperPayStep === "sync" && (
                        <div style={{ padding: "20px" }}>
                            <Typography style={{ marginBottom: "20px" }}>
                                <strong>{payeeName}</strong> is not yet registered for external payments. 
                                Would you like to sync their profile now?
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "20px" }}>
                                This will register the employee using their superannuation details.
                            </Typography>
                        </div>
                    )}

                    {!hgSuperPayLoading && !hgSuperPayError && hgSuperPayStep === "confirm" && (
                        <div style={{ padding: "20px" }}>
                            <Typography variant="h6" style={{ marginBottom: "20px" }}>
                                Confirm Payment
                            </Typography>
                            <div style={{ backgroundColor: "#f5f5f5", padding: "15px", borderRadius: "4px", marginBottom: "20px" }}>
                                <Typography variant="body2" color="textSecondary">Payee</Typography>
                                <Typography style={{ fontWeight: "bold", marginBottom: "10px" }}>{payeeName}</Typography>
                                
                                <Typography variant="body2" color="textSecondary">Number of Ledgers</Typography>
                                <Typography style={{ fontWeight: "bold", marginBottom: "10px" }}>{selectedLedgerIds.length}</Typography>
                                
                                <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                                <Typography style={{ fontWeight: "bold", fontSize: "20px", color: "#ff0c9c" }}>
                                    {currencySymbol}{currencyHelper.formatMoney(selectedTotal, 2)}
                                </Typography>
                            </div>
                            <TextField
                                label="Notes (optional)"
                                value={this.state.paymentNotes}
                                onChange={this.handlePaymentNotesChange}
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                placeholder="Payment notes..."
                            />
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    {!hgSuperPayLoading && !hgSuperPayError && (
                        <>
                            <Button onClick={this.handleCloseHgSuperPayDialog}>Cancel</Button>
                            {hgSuperPayStep === "sync" && (
                                <Button 
                                    onClick={this.handleSyncEmployee} 
                                    color="primary" 
                                    variant="contained"
                                >
                                    Sync Employee
                                </Button>
                            )}
                            {hgSuperPayStep === "confirm" && (
                                <Button 
                                    onClick={this.handleSubmitHgSuperPayPayment} 
                                    color="primary" 
                                    variant="contained"
                                >
                                    Submit Payment
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>
        );
    };

    renderPaymentDialog = () => {
        const { paymentDialogOpen, paymentDate, paymentNotes, selectedLedgerIds, isSubmitting } = this.state;
        const unpaidLedgers = this.getUnpaidLedgers();
        const currencySymbol = this.getCurrencySymbol();

        const selectedTotal = unpaidLedgers
            .filter(l => selectedLedgerIds.includes(l.id))
            .reduce((sum, l) => sum + parseFloat(l.superFee || 0), 0);

        return (
            <Dialog
                open={paymentDialogOpen}
                onClose={this.handleClosePaymentDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Mark as Paid Manually</DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: "20px" }}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <DatePicker
                                label="Payment Date"
                                value={paymentDate}
                                onChange={this.handlePaymentDateChange}
                                format="DD/MM/YYYY"
                                fullWidth
                                margin="normal"
                                keyboardIcon={<EventIcon />}
                                leftArrowIcon={<KeyboardArrowLeftIcon />}
                                rightArrowIcon={<KeyboardArrowRightIcon />}
                            />
                        </MuiPickersUtilsProvider>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <TextField
                            label="Notes"
                            value={paymentNotes}
                            onChange={this.handlePaymentNotesChange}
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            placeholder="Enter payment notes..."
                        />
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                        <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: "10px" }}>
                            Select Ledgers to Pay ({selectedLedgerIds.length} of {unpaidLedgers.length} selected)
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedLedgerIds.length === unpaidLedgers.length && unpaidLedgers.length > 0}
                                    indeterminate={selectedLedgerIds.length > 0 && selectedLedgerIds.length < unpaidLedgers.length}
                                    onChange={this.handleSelectAllLedgers}
                                    color="primary"
                                />
                            }
                            label="Select All"
                        />
                    </div>

                    <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ backgroundColor: "#f5f5f5" }}>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}></th>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Date</th>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Booking</th>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Invoice</th>
                                    <th style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #ddd" }}>Super Fee</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unpaidLedgers.map((ledger) => {
                                    const booking = ledger.booking || {};
                                    const bookingDate = booking.dateStart && moment.utc(booking.dateStart).isValid()
                                        ? moment.utc(booking.dateStart).format("DD MMM YYYY")
                                        : 'N/A';

                                    return (
                                        <tr key={ledger.id} style={{ backgroundColor: selectedLedgerIds.includes(ledger.id) ? "#e3f2fd" : "#fff" }}>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                <Checkbox
                                                    checked={selectedLedgerIds.includes(ledger.id)}
                                                    onChange={() => this.handleLedgerCheckboxChange(ledger.id)}
                                                    color="primary"
                                                />
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>{bookingDate}</td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>{booking.title || 'N/A'}</td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>HG-{ledger.invoice?.no || 'N/A'}</td>
                                            <td style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #eee" }}>
                                                {currencySymbol}{currencyHelper.formatMoney(ledger.superFee, 2)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                        <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                            Total Selected: {currencySymbol}{currencyHelper.formatMoney(selectedTotal, 2)}
                        </Typography>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleClosePaymentDialog} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={this.handleSubmitPayment} 
                        color="primary" 
                        variant="contained"
                        disabled={selectedLedgerIds.length === 0 || isSubmitting}
                    >
                        {isSubmitting ? "Processing..." : "Confirm Payment"}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    render() {
        const { open, onClose, ledgerType } = this.props;
        const currencySymbol = this.getCurrencySymbol();
        const filteredLedgers = this.getFilteredLedgers();
        const firstPayment = filteredLedgers.length > 0 ? filteredLedgers[0].payment : null;
        const payment = this.props.payment || firstPayment;
        const unpaidLedgers = this.getUnpaidLedgers();
        const summary = this.calculateSummary(filteredLedgers);
        const dateRangeText = this.getDateRangeText();

        const { currentProfile } = this.props.profileStore;
        const isStaff = currentProfile?.type === ProfileType.Staff.ordinal;
        const isSingleVenueStaff = isStaff && !currentProfile?.hasOrganisation;
        const isOrgStaff = isStaff && currentProfile?.hasOrganisation;

        return (
            <ModalContainer
                open={open}
                onClose={onClose}
                title="Super Ledger Report"
                maxWidth="md"
                coloredDialogTitle={true}
            >
                <DialogContent style={{ paddingTop: 24 }}>
                    {filteredLedgers.some(l => l.status === SuperLedgerStatus.Refunded.ordinal || l.status === SuperLedgerStatus.Failed.ordinal) && (
                        <div style={{ backgroundColor: "#FFF8E1", border: "1px solid #FFB300", borderRadius: 6, padding: "12px 16px", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <span style={{ fontSize: 18, lineHeight: 1.2 }}>⚠</span>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: "#E65100", marginBottom: 4 }}>Previous payment attempt on record</div>
                                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>
                                    One or more ledgers in this report were previously submitted for payment but the payment was refunded or failed. These ledgers are still eligible — select them below and submit a new payment to retry.
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={this.printRef} style={printStyles.container}>
                        <div style={printStyles.header}>
                            <div style={printStyles.title}>
                                Superannuation Ledger Report
                            </div>
                            <div style={printStyles.subtitle}>
                                {ledgerType === "outgoing" ? "Super Owed To Me" : "Super I Owe"}
                            </div>
                            {(() => {
                                const payer = filteredLedgers[0]?.payer;
                                const payerName = payer
                                    ? (payer.profileName || `${payer.firstName || ''} ${payer.lastName || ''}`.trim() || null)
                                    : null;
                                return payerName ? (
                                    <div style={printStyles.bodyText}>
                                        Payer: {payerName}
                                    </div>
                                ) : null;
                            })()}
                            <div style={printStyles.bodyText}>
                                Period: {dateRangeText}
                            </div>
                        </div>

                        <div style={printStyles.divider} />

                        <div style={{ marginBottom: "30px", backgroundColor: "#ffffff" }}>
                            <div style={printStyles.sectionTitle}>
                                Summary
                            </div>
                            
                            <div style={printStyles.summaryBox}>
                                <div style={printStyles.gridRow}>
                                    <div style={printStyles.gridCol6}>
                                        <div style={printStyles.summaryLabel}>
                                            Total Entries (with invoices)
                                        </div>
                                        <div style={printStyles.summaryValue}>
                                            {summary.ledgerCount}
                                        </div>
                                    </div>
                                    <div style={printStyles.gridCol6}>
                                        <div style={printStyles.summaryLabel}>
                                            Total Super Payable
                                        </div>
                                        <div style={printStyles.summaryTotal}>
                                            {currencySymbol}{currencyHelper.formatMoney(summary.totalSuperFee, 2)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {Object.keys(summary.payeeGroups).length > 0 && (
                                <div style={{ backgroundColor: "#ffffff" }}>
                                    <div style={{ ...printStyles.sectionTitle, fontSize: "16px" }}>
                                        Super Fund Payment Details
                                    </div>
                                    {Object.values(summary.payeeGroups).map((group, index) => (
                                        <div key={index} style={printStyles.paymentCard}>
                                            <div style={printStyles.gridRow}>
                                                <div style={printStyles.gridCol6}>
                                                    <div style={printStyles.paymentLabel}>Member Name</div>
                                                    <div style={printStyles.paymentValueBold}>{group.payeeName}</div>
                                                </div>
                                                <div style={printStyles.gridCol6}>
                                                    <div style={printStyles.paymentLabel}>Amount to Pay</div>
                                                    <div style={printStyles.paymentAmount}>
                                                        {currencySymbol}{currencyHelper.formatMoney(group.totalAmount, 2)}
                                                    </div>
                                                </div>
                                            </div>
                                            {(group.bsb !== 'N/A' || group.accountNumber !== 'N/A' || group.accountName !== 'N/A') && (
                                                <div style={printStyles.gridRow}>
                                                    {group.bsb !== 'N/A' && (
                                                        <div style={printStyles.gridCol6}>
                                                            <div style={printStyles.paymentLabel}>
                                                                {group.currency === 'USD' ? 'Routing Number' : group.currency === 'GBP' ? 'Sort Code' : group.currency === 'NZD' ? 'Bank Code' : 'BSB'}
                                                            </div>
                                                            <div style={printStyles.paymentValue}>{group.bsb}</div>
                                                        </div>
                                                    )}
                                                    {group.accountNumber !== 'N/A' && (
                                                        <div style={printStyles.gridCol6}>
                                                            <div style={printStyles.paymentLabel}>Account Number</div>
                                                            <div style={printStyles.paymentValue}>{group.accountNumber}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {group.accountName !== 'N/A' && (
                                                <div style={printStyles.gridRow}>
                                                    <div style={printStyles.gridCol6}>
                                                        <div style={printStyles.paymentLabel}>Account Name</div>
                                                        <div style={printStyles.paymentValue}>{group.accountName}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {group.id && group.id !== 'unknown' && (() => {
                                                const hgEntry = this.state.hgSuperPayData[group.id];
                                                if (!hgEntry) return null;
                                                if (hgEntry.loading) {
                                                    return (
                                                        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                                                            <CircularProgress size={16} style={{ color: "#ff0c9c" }} />
                                                            <span style={{ color: "#888", fontSize: 12 }}>Loading HG Super Pay details...</span>
                                                        </div>
                                                    );
                                                }
                                                if (hgEntry.error || !hgEntry.data) {
                                                    return (
                                                        <div style={{ marginTop: 12, color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
                                                            Not registered with HG Super Pay
                                                        </div>
                                                    );
                                                }
                                                const emp = hgEntry.data;
                                                const addr = formatHgAddress(emp);
                                                return (
                                                    <Fragment>
                                                        <div style={{ borderTop: "1px solid #eeeeee", margin: "12px 0 10px" }} />
                                                        <div style={{ ...printStyles.paymentLabel, fontWeight: 700, color: "#ff0c9c", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                            HG Super Pay Details
                                                        </div>
                                                        <div style={printStyles.gridRow}>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>First Name</div>
                                                                <div style={printStyles.paymentValue}>{emp.firstName || "—"}</div>
                                                            </div>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Last Name</div>
                                                                <div style={printStyles.paymentValue}>{emp.lastName || "—"}</div>
                                                            </div>
                                                        </div>
                                                        <div style={printStyles.gridRow}>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Email</div>
                                                                <div style={printStyles.paymentValue}>
                                                                    {emp.email ? <a href={`mailto:${emp.email}`} style={{ color: "#1976d2", textDecoration: "none" }}>{emp.email}</a> : "—"}
                                                                </div>
                                                            </div>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Date of Birth</div>
                                                                <div style={printStyles.paymentValue}>{formatHgDob(emp) || "—"}</div>
                                                            </div>
                                                        </div>
                                                        <div style={printStyles.gridRow}>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Employee ABN</div>
                                                                <div style={printStyles.paymentValue}>{emp.employeeAbn || "—"}</div>
                                                            </div>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Tax File Number</div>
                                                                <div style={printStyles.paymentValue}>{(emp.hasTfn || emp.taxFileNumber) ? "*********" : "—"}</div>
                                                            </div>
                                                        </div>
                                                        {addr ? (
                                                            <div style={printStyles.gridRow}>
                                                                <div style={{ width: "100%", padding: "5px", boxSizing: "border-box" }}>
                                                                    <div style={printStyles.paymentLabel}>Address</div>
                                                                    <div style={printStyles.paymentValue}>{addr}</div>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        <div style={printStyles.gridRow}>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Fund</div>
                                                                <div style={printStyles.paymentValue}>{emp.fundName || "—"}</div>
                                                            </div>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Product</div>
                                                                <div style={printStyles.paymentValue}>{emp.productName || "—"}</div>
                                                            </div>
                                                        </div>
                                                        <div style={printStyles.gridRow}>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>USI</div>
                                                                <div style={printStyles.paymentValue}>{emp.usi || "—"}</div>
                                                            </div>
                                                            <div style={printStyles.gridCol6}>
                                                                <div style={printStyles.paymentLabel}>Member Number</div>
                                                                <div style={printStyles.paymentValue}>{emp.memberNumber || "—"}</div>
                                                            </div>
                                                        </div>
                                                        {emp.fundAbn ? (
                                                            <div style={printStyles.gridRow}>
                                                                <div style={printStyles.gridCol6}>
                                                                    <div style={printStyles.paymentLabel}>Fund ABN</div>
                                                                    <div style={printStyles.paymentValue}>{emp.fundAbn}</div>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </Fragment>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={printStyles.divider} />

                        <div style={{ backgroundColor: "#ffffff" }}>
                            <div style={printStyles.sectionTitle}>
                                Ledger Details ({filteredLedgers.length} entries)
                            </div>
                            
                            {filteredLedgers.length > 0 ? (
                                <table style={printStyles.table}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#f5f5f5" }}>
                                            <th style={{ ...printStyles.tableHeader, width: "10%" }}>Date</th>
                                            <th style={{ ...printStyles.tableHeader, width: "14%" }}>Payee Contact Name</th>
                                            <th style={{ ...printStyles.tableHeader, width: "22%" }}>Booking</th>
                                            <th style={{ ...printStyles.tableHeader, width: "8%" }}>Invoice #</th>
                                            <th style={{ ...printStyles.tableHeaderRight, width: "10%" }}>Super Amount</th>
                                            <th style={{ ...printStyles.tableHeader, width: "9%", textAlign: "center" }}>Super Pay Ready</th>
                                            {isStaff ? null : <th style={{ ...printStyles.tableHeader, width: "9%", textAlign: "center" }}>Ready to Pay</th>}
                                            <th style={{ ...printStyles.tableHeader, width: "13%" }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLedgers.map((ledger, index) => {
                                            const booking = ledger.booking || {};
                                            const payee = ledger.payee || {};
                                            const payeeName = payee.profileName || `${payee.firstName || ''} ${payee.lastName || ''}`.trim() || 'N/A';

                                            const bookingDate = booking.dateStart && moment.utc(booking.dateStart).isValid()
                                                ? moment.utc(booking.dateStart).format("DD MMM YYYY") : "N/A";
                                            const bookingLabel = booking.title || booking.name || "N/A";
                                            const bookingCell = booking.id ? (
                                                <a href={`/booking/${booking.id}/details`} style={{ color: "#1976d2", textDecoration: "none" }}>
                                                    {bookingLabel}
                                                </a>
                                            ) : bookingLabel;

                                            const invoiceNo = ledger.invoice?.no ? `HG-${ledger.invoice.no}` : "N/A";
                                            const paymentType = isOrgStaff ? 2 :
                                                (this.props.financialProfileStore.paymentType ??
                                                    (isSingleVenueStaff ? (currentProfile?.venue?.paymentType ?? null) : (currentProfile?.paymentType ?? null)) ??
                                                    null);

                                            // const isSuperComplete = ledger.isSuperAnnuationComplete || ledger.isSuperAnnuationCompleteXero;
                                            // const superReadyCell = isSuperComplete ? (
                                            //     <Tooltip title="Super details added — ready to pay">
                                            //         <CheckCircleIcon style={{ color: "#4CAF50" }} />
                                            //     </Tooltip>
                                            // ) : (
                                            //     <Tooltip title="Super details not yet complete">
                                            //         <WarningIcon style={{ color: "#ff9800" }} />
                                            //     </Tooltip>
                                            // );

                                            const isSuperComplete = paymentType === 1
                                                ? ledger.isSuperAnnuationCompleteXero
                                                : ledger.isSuperAnnuationComplete;
                                            const isHgSuperPayActive = paymentType === 2 && !!this.props.hgBillingStore.billingAccess?.hasActivePaymentSubscription;
                                            const superReadyCell = isSuperComplete ? (
                                                <Tooltip title="Super details added — ready to pay">
                                                    <CheckCircleIcon style={{ color: "#4CAF50" }} />
                                                </Tooltip>
                                            ) : (
                                                    <Tooltip title={ledger.mvrErrorCode === "EMPLOYEE_NOT_SYNCED" ? "Employee Not Yet Registered" : isHgSuperPayActive ? "Not ready to pay" : paymentType === 1 ? "Artist not yet set up in Xero" : "Super details not yet added on this artist's profile"}>
                                                    <WarningIcon style={{ color: "#ff9800" }} />
                                                </Tooltip>
                                            );

                                            const invoice = ledger.invoice || {};
                                            const isInvoicePaid = invoice.status === "paid" || !!ledger.isInvoicePaid;
                                            const notReadyReason = !isInvoicePaid
                                                ? "Related outgoing invoice not yet paid"
                                                : !isSuperComplete
                                                    ? paymentType === 1 ? "Artist not yet set up in Xero" : "Super details not yet added"
                                                    : "Not ready to pay";

                                            const readyToPayCell = ledger.isReadyToPay ? (
                                                <Tooltip title="Venue invoice is paid and super details are complete — ready to pay">
                                                    <CheckCircleIcon style={{ color: "#4CAF50" }} />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={notReadyReason}><WarningIcon style={{ color: "#ff9800" }} /></Tooltip>
                                            );

                                            let statusText = "Pending";
                                            if (ledger.status === SuperLedgerStatus.Paid.ordinal) statusText = "Paid";
                                            else if (ledger.status === SuperLedgerStatus.Posted.ordinal) statusText = "Posted";
                                            else if (ledger.status === SuperLedgerStatus.Processing.ordinal) statusText = "Processing";
                                            else if (ledger.status === SuperLedgerStatus.Refunded.ordinal) statusText = "Refunded";
                                            else if (ledger.status === SuperLedgerStatus.Failed.ordinal) statusText = "Failed";

                                            const pd = ledger.paymentDetail;
                                            const pdStatus = pd?.status;
                                            const pdIsFailure = pdStatus && ["refunded", "failed", "debit_failed", "fund_transfer_failed"].includes(pdStatus.toLowerCase());
                                            const pdFailureReason = pd?.failureDetailedReason || pd?.failureReason;
                                            const pdChipColors = {
                                                paid:                 { bg: "#E8F5E9", color: "#2E7D32" },
                                                settled:              { bg: "#E8F5E9", color: "#2E7D32" },
                                                submitted:            { bg: "#E3F2FD", color: "#1565C0" },
                                                processing:           { bg: "#E3F2FD", color: "#1565C0" },
                                                refunded:             { bg: "#FFEBEE", color: "#C62828" },
                                                failed:               { bg: "#FFEBEE", color: "#C62828" },
                                                debit_failed:         { bg: "#FFEBEE", color: "#C62828" },
                                                fund_transfer_failed: { bg: "#FFEBEE", color: "#C62828" },
                                            };
                                            const pdChipStyle = pdStatus ? (pdChipColors[pdStatus.toLowerCase()] || { bg: "#EEEEEE", color: "#616161" }) : null;

                                            return (
                                                <tr key={ledger.id || index} style={{ backgroundColor: "#ffffff" }}>
                                                    <td style={printStyles.tableCell}>{bookingDate}</td>
                                                    <td style={printStyles.tableCell}>{payeeName}</td>
                                                    <td style={printStyles.tableCell}>{bookingCell}</td>
                                                    <td style={printStyles.tableCell}>{invoiceNo}</td>
                                                    <td style={printStyles.tableCellRight}>{currencySymbol}{currencyHelper.formatMoney(ledger.superFee, 2)}</td>
                                                    <td style={{ ...printStyles.tableCell, textAlign: "center" }}>{superReadyCell}</td>
                                                    {isStaff ? null : <td style={{ ...printStyles.tableCell, textAlign: "center" }}>{readyToPayCell}</td>}
                                                    <td style={printStyles.tableCell}>
                                                        <div>{statusText}</div>
                                                        {pdStatus && pdChipStyle && (
                                                            <div style={{ marginTop: 4 }}>
                                                                <Chip
                                                                    label={pdStatus}
                                                                    style={{ backgroundColor: pdChipStyle.bg, color: pdChipStyle.color, fontWeight: 700, fontSize: 10, height: 18 }}
                                                                />
                                                                {pdIsFailure && pdFailureReason && (
                                                                    <div style={{ fontSize: 11, color: "#C62828", marginTop: 3, lineHeight: 1.4 }}>
                                                                        {pdFailureReason}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        <tr style={{ backgroundColor: "#f5f5f5" }}>
                                            <td colSpan={4} style={printStyles.tableFooter}>Total</td>
                                            <td style={printStyles.tableFooterRight}>{currencySymbol}{currencyHelper.formatMoney(summary.totalSuperFee, 2)}</td>
                                            <td colSpan={3} style={printStyles.tableCell}></td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <div style={printStyles.emptyMessage}>
                                    No ledger entries with invoices found for this period.
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                    {/* HGPay integration hidden - uncomment when ready
                    {unpaidLedgers.length > 0 && (
                        <Button 
                            onClick={this.handleOpenHgSuperPayDialog} 
                            color="primary" 
                            variant="contained"
                            style={{ backgroundColor: "#4CAF50" }}
                        >
                            Process Payment
                        </Button>
                    )}
                    */}
                    <ReactToPrint
                        trigger={() => (
                            <Button color="primary" variant="contained">
                                Download / Print PDF
                            </Button>
                        )}
                        content={() => this.printRef.current}
                        documentTitle={`Super_Ledger_Report_${moment().format("YYYY-MM-DD")}`}
                        copyStyles={false}
                        pageStyle={`
                            @page { 
                                background: white !important; 
                                background-color: white !important;
                                margin: 15mm; 
                                size: A4;
                            }
                            html {
                                background: white !important;
                                background-color: white !important;
                            }
                            body {
                                background: white !important;
                                background-color: white !important;
                                color: black !important;
                                font-family: Arial, sans-serif !important;
                                margin: 0 !important;
                                padding: 0 !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            * {
                                box-sizing: border-box !important;
                            }
                            table {
                                width: 100% !important;
                                border-collapse: collapse !important;
                                background: white !important;
                                background-color: white !important;
                            }
                            th, td {
                                padding: 10px 8px !important;
                                text-align: left !important;
                                border-bottom: 1px solid #ddd !important;
                                background: white !important;
                                background-color: white !important;
                                color: black !important;
                            }
                            th {
                                background-color: #f5f5f5 !important;
                                font-weight: bold !important;
                            }
                            tr {
                                background: white !important;
                                background-color: white !important;
                            }
                        `}
                    />
                </DialogActions>
                {this.renderPaymentDialog()}
                {this.renderHgSuperPayDialog()}
            </ModalContainer>
        );
    }
}

export default SuperLedgerReportModal;
